import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { CommonService } from '@core/services/common.service';
import { NavigationEnd, Router } from '@angular/router';
import { DataService } from './core/services/data.service';
import { UserService } from './core/services/user.service';
import { AuthService } from 'src/app/core/services/adal.service';
import { BnNgIdleService } from 'bn-ng-idle';
import { RELOAD_MESSAGE, RELOAD_TIME } from '@shared/shared.constant';
import { SharedService } from '@core-services/shared.service';
import { environment } from '../environments/environment';

import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
@Component({
  selector: 'gdx-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly LOGIN_REDIRECT_ATTEMPT_KEY =
    'sip_login_redirect_attempted';
  loggedIn = false;
  roleList: string[] = [];
  isNavLoad = false;
  isAppLoad = true;
  isNoRoleModal = false;
  isGdxPlatformModal = false;
  timer: any;
  selectedRole = '';
  sasToken = '';
  editSasToken = '';
  footerVisible = true;
  sessionExpired = false;
  reload_message = RELOAD_MESSAGE;
  isConfigLoaded = false;
  appConfigSubscriber;
  accessGranted = false;
  accessDenied = false;
  private loginRedirectTriggered = false;
  private idleWatcherStarted = false;
  private readonly subscriptions = new Subscription();
  constructor(
    private translate: TranslateService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private commonService: CommonService,
    private router: Router,
    private dataService: DataService,
    private userService: UserService,
    private bnIdle: BnNgIdleService,
    public AuthSer: AuthService,
    private sharedService: SharedService
  ) {
    this.updateVisible();
    this.translate.setDefaultLang('en');
    this.appConfigSubscriber = this.sharedService.appConfigData.subscribe(
      () => {
        this.isConfigLoaded = true;
      }
    );
    this.subscriptions.add(this.appConfigSubscriber);
  }

  private initializeUserState(): void {
    let token = this.authService.instance.getActiveAccount();
    if (!token) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.authService.instance.setActiveAccount(accounts[0]);
        token = accounts[0];
      }
    }

    if (token) {
      this.loggedIn = true;
      this.sessionExpired = false;
      this.loginRedirectTriggered = false;
      sessionStorage.removeItem(AppComponent.LOGIN_REDIRECT_ATTEMPT_KEY);
      if (!this.idleWatcherStarted) {
        this.bnIdle.stopTimer();
        this.startWatching();
        this.idleWatcherStarted = true;
      }
    } else {
      if (this.loginRedirectTriggered) {
        return;
      }
      this.loginRedirectTriggered = true;

      // Guard against an infinite login redirect loop: MSAL performs a full
      // page redirect, so an in-memory flag alone won't survive the reload.
      // If we've already attempted a redirect in this browser tab and still
      // have no account, MSAL is failing to complete sign-in (commonly a
      // redirect URI not registered for this environment's app registration
      // in Azure AD). Stop looping instead of repeatedly reloading the page.
      if (sessionStorage.getItem(AppComponent.LOGIN_REDIRECT_ATTEMPT_KEY)) {
        console.error(
          'SIP: Login redirect already attempted but no account was found. ' +
            'This usually means the redirect URI used by the app is not ' +
            'registered on the Azure AD app registration for this environment. ' +
            'Aborting further redirects to prevent a reload loop.'
        );
        return;
      }
      sessionStorage.setItem(AppComponent.LOGIN_REDIRECT_ATTEMPT_KEY, '1');
      this.authService.loginRedirect(environment.msalLoginReq);
      return;
    }

    if (token?.idTokenClaims) {
      this.roleList = token.idTokenClaims.roles || [];
      this.roleList = this.roleList.filter(
        (item: any) =>
          !item.toUpperCase().includes('APPACCESS') &&
          !item.toUpperCase().includes('DATAACCESS')
      );
    }
  }

  startWatching(): void {
    const tokenExpirySeconds = this.AuthSer.calculateExpirySec();
    const idleSeconds = Math.min(tokenExpirySeconds, RELOAD_TIME);
    const timeoutSeconds =
      Number.isFinite(idleSeconds) && idleSeconds > 0
        ? idleSeconds
        : RELOAD_TIME;
    this.subscriptions.add(
      this.bnIdle.startWatching(timeoutSeconds).subscribe((res) => {
        this.sessionExpired = !!res;
        if (res) {
          this.idleWatcherStarted = false;
        }
      })
    );
  }

  onActivate(event: any): void {
    window.scroll(0, 0);
  }

  @HostListener('window:mousemove')
  onWindowMousemove(): void {
    this.clearTimer();
  }
  @HostListener('window:mousewheel')
  onWindowMouseWheel(): void {
    this.clearTimer();
  }
  @HostListener('window:keyup')
  onWindowKeyup(): void {
    this.clearTimer();
  }
  @HostListener('window:keydown')
  onWindowKeydown(): void {
    this.clearTimer();
  }
  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.clearTimer();
  }
  @HostListener('window:click')
  onWindowClick(): void {
    this.clearTimer();
  }

  clearTimer(): void {
    clearTimeout(this.timer);
  }

  startTimer(): void {
    this.timer = setTimeout(() => {
      sessionStorage.clear();
      this.authService.logoutRedirect();
    }, 900000);
  }

  ngOnInit(): void {
    // MSAL v5 removed `navigateToLoginRequestUrl` from the auth config; it is
    // now passed to handleRedirectObservable. Setting it to false keeps the app
    // on the redirectUri and strips the `?state=...` param so we land on a clean
    // URL (e.g. /home) instead of a blank page after login.
    this.subscriptions.add(
      this.authService
        .handleRedirectObservable({ navigateToLoginRequestUrl: false })
        .subscribe()
    );

    this.subscriptions.add(
      this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          )
        )
        .subscribe(() => {
          this.initializeUserState();
        })
    );

    this.authService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        this.loggedIn = false;
      }
    });

    this.authService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        this.loggedIn = true;
        this.sessionExpired = false;
        sessionStorage.removeItem(AppComponent.LOGIN_REDIRECT_ATTEMPT_KEY);
      }
    });

    this.authService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        // Surfaces the exact AADSTS error/description so the real cause
        // (e.g. redirect URI/platform mismatch, consent required, etc.)
        // is visible instead of just seeing the app abort the retry loop.
        console.error('SIP: MSAL ACQUIRE_TOKEN_FAILURE', event.error);
      }
    });

    this.subscriptions.add(
      this.dataService.isNavigationLoad.subscribe((result: boolean) => {
        this.isNavLoad = result;
        this.accessGranted =
          this.isNavLoad && this.isAppLoad && this.isConfigLoaded
            ? true
            : false;
        this.accessDenied = this.accessGranted ? false : true;
      })
    );

    this.subscriptions.add(
      this.dataService.isAppLoad.subscribe((result: boolean) => {
        this.isAppLoad = result;
        this.selectedRole = this.userService.getCurrentUserRole()?.name;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    if (event.ctrlKey && event.altKey && event.keyCode === 86) {
      this.isGdxPlatformModal = true;
    }
  }

  close($event: any): void {
    this.isGdxPlatformModal = false;
  }
  updateVisible(): void {
    this.subscriptions.add(
      this.router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          this.footerVisible = this.commonService.headerFooterVisibility(
            this.router
          );
        }
      })
    );
  }
}
